import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/utils";

const CFG = {
  clients:   { subtitle: "◇ ADMIN · CLIENTS",   title: "All clients",    endpoint: "/admin/clients" },
  payments:  { subtitle: "◇ ADMIN · PAYMENTS",  title: "Every invoice",  endpoint: "/admin/payments" },
  analytics:  { subtitle: "◇ ADMIN · ANALYTICS",  title: "Business pulse", endpoint: "/admin/stats" },
  "ai-usage": { subtitle: "◇ ADMIN · AI USAGE", title: "AI usage & cost", endpoint: "/admin/ai-usage" },
  "audit-logs": { subtitle: "◇ ADMIN · AUDIT LOGS", title: "Operational history", endpoint: "/admin/audit-logs" },
};

export default function AdminSection() {
  const { section } = useParams();
  const cfg = CFG[section];
  const [result, setResult] = useState({ section: null, data: null });
  const data = result.section === section ? result.data : null;

  useEffect(() => {
    let cancelled = false;
    setResult({ section, data: null });

    if (!cfg) return () => { cancelled = true; };

    api.get(cfg.endpoint)
      .then(({ data: nextData }) => {
        if (cancelled) return;

        const normalizedData = ["analytics", "ai-usage"].includes(section)
          ? (
              nextData &&
              typeof nextData === "object" &&
              !Array.isArray(nextData)
                ? nextData
                : null
            )
          : (Array.isArray(nextData) ? nextData : []);

        setResult({ section, data: normalizedData });
      })
      .catch(() => {
        if (!cancelled) {
          setResult({
            section,
            data: ["analytics", "ai-usage"].includes(section) ? null : [],
          });
        }
      });

    return () => { cancelled = true; };
  }, [cfg, section]);

  if (!cfg) return <AppShell><div className="p-10">Unknown section</div></AppShell>;

  return (
    <AppShell subtitle={cfg.subtitle} title={cfg.title}>
      {section === "clients" && (
        <div className="border border-black/10">
          <div className="grid grid-cols-12 border-b border-black/10 bg-[#F4F6F8] px-5 py-3 fx-mono-tag">
            <div className="col-span-4">CLIENT</div><div className="col-span-3">EMAIL</div>
            <div className="col-span-2">COMPANY</div><div className="col-span-2">JOINED</div>
            <div className="col-span-1 text-right">PROJECTS</div>
          </div>
          {(data || []).map(c => (
            <div key={c.id} className="grid grid-cols-12 px-5 py-4 items-center border-b border-black/5" data-testid={`admin-client-${c.id}`}>
              <div className="col-span-4 font-semibold text-sm">{c.name}</div>
              <div className="col-span-3 text-xs font-mono text-neutral-600">{c.email}</div>
              <div className="col-span-2 text-sm">{c.company || "—"}</div>
              <div className="col-span-2 text-xs font-mono text-neutral-500">{formatDate(c.created_at)}</div>
              <div className="col-span-1 text-right font-mono font-semibold">{c.project_count}</div>
            </div>
          ))}
          {data && data.length === 0 && <div className="p-12 text-center text-neutral-500">No clients yet.</div>}
        </div>
      )}

      {section === "payments" && (
        <div className="border border-black/10">
          <div className="grid grid-cols-12 border-b border-black/10 bg-[#F4F6F8] px-5 py-3 fx-mono-tag">
            <div className="col-span-3">INVOICE</div><div className="col-span-4">PROJECT</div>
            <div className="col-span-2">ISSUED</div><div className="col-span-1 text-right">AMOUNT</div>
            <div className="col-span-2 text-right">STATUS</div>
          </div>
          {(data || []).map(inv => (
            <div key={inv.id} className="grid grid-cols-12 px-5 py-4 items-center border-b border-black/5" data-testid={`admin-payment-${inv.id}`}>
              <div className="col-span-3 font-mono text-sm font-semibold">{inv.number}</div>
              <div className="col-span-4 text-sm truncate">{inv.project_title}</div>
              <div className="col-span-2 text-xs font-mono text-neutral-500">{formatDate(inv.issued_at)}</div>
              <div className="col-span-1 text-right font-mono font-semibold">{formatCurrency(inv.amount_usd, inv.currency)}</div>
              <div className="col-span-2 text-right">
                <span className="fx-badge" style={{
                  background: inv.status === "paid" ? "#E4F5EA" : "#FFF7D6",
                  color: inv.status === "paid" ? "#1E7C3B" : "#8A6A00",
                  borderColor: inv.status === "paid" ? "#34C759" : "#FFCC00",
                }}>{inv.status.toUpperCase()}</span>
              </div>
            </div>
          ))}
          {data && data.length === 0 && <div className="p-12 text-center text-neutral-500">No payments yet.</div>}
        </div>
      )}

      {section === "ai-usage" && data && (
        <div className="space-y-8" data-testid="admin-ai-usage">
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-black/10">
            <div
              className="border-r border-b border-black/10 p-6 bg-[#0B1F3A] text-white"
              data-testid="ai-usage-total-calls"
            >
              <div className="fx-mono-tag" style={{ color: "#a3a3a3" }}>
                CALLS · {data.period_days} DAYS
              </div>
              <div className="mt-3 text-4xl font-black tracking-tighter font-mono">
                {Number(data.summary?.event_count || 0).toLocaleString()}
              </div>
            </div>

            <div
              className="border-r border-b border-black/10 p-6"
              data-testid="ai-usage-total-tokens"
            >
              <div className="fx-mono-tag">REPORTED TOKENS</div>
              <div className="mt-3 text-4xl font-black tracking-tighter font-mono">
                {Number(data.summary?.total_tokens || 0).toLocaleString()}
              </div>
            </div>

            <div
              className="border-r border-b border-black/10 p-6"
              data-testid="ai-usage-failures"
            >
              <div className="fx-mono-tag">FAILURES</div>
              <div className="mt-3 text-4xl font-black tracking-tighter font-mono">
                {Number(data.summary?.failure_count || 0).toLocaleString()}
              </div>
            </div>

            <div
              className="border-r border-b border-black/10 p-6"
              data-testid="ai-usage-known-cost"
            >
              <div className="fx-mono-tag">KNOWN COST</div>
              <div className="mt-3 text-4xl font-black tracking-tighter font-mono">
                {formatCurrency(
                  Number(data.summary?.known_cost_microusd || 0) / 1000000,
                  "USD"
                )}
              </div>
            </div>
          </div>

          {Number(data.summary?.unknown_cost_events || 0) > 0 && (
            <div className="border border-[#FFCC00] bg-[#FFF7D6] px-5 py-4 text-sm">
              <span className="font-mono font-semibold">
                {Number(data.summary.unknown_cost_events).toLocaleString()}
              </span>
              {" "}call(s) have unavailable provider pricing and are excluded
              from known cost.
            </div>
          )}

          <div className="border border-black/10">
            <div className="grid grid-cols-12 border-b border-black/10 bg-[#F4F6F8] px-5 py-3 fx-mono-tag">
              <div className="col-span-2">TIME</div>
              <div className="col-span-3">OPERATION</div>
              <div className="col-span-2">PROVIDER</div>
              <div className="col-span-2 text-right">TOKENS</div>
              <div className="col-span-1 text-right">LATENCY</div>
              <div className="col-span-2 text-right">RESULT</div>
            </div>

            {(data.events || []).map(event => (
              <div
                key={event.id}
                className="grid grid-cols-12 px-5 py-4 items-center border-b border-black/5"
                data-testid={`ai-usage-event-${event.id}`}
              >
                <div className="col-span-2 text-xs font-mono text-neutral-500">
                  {formatDateTime(event.created_at)}
                </div>

                <div className="col-span-3">
                  <div className="text-sm font-mono font-semibold">
                    {event.operation}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {event.feature}
                  </div>
                </div>

                <div className="col-span-2">
                  <div className="text-sm font-semibold">{event.provider}</div>
                  <div className="text-xs font-mono text-neutral-500 truncate">
                    {event.model}
                  </div>
                </div>

                <div className="col-span-2 text-right font-mono text-sm">
                  {event.usage_source === "reported"
                    ? Number(event.total_tokens || 0).toLocaleString()
                    : "—"}
                </div>

                <div className="col-span-1 text-right font-mono text-xs">
                  {event.latency_ms == null ? "—" : `${event.latency_ms} ms`}
                </div>

                <div className="col-span-2 text-right">
                  <span
                    className="fx-badge"
                    style={{
                      background: event.status === "success" ? "#E4F5EA" : "#FFE8E6",
                      color: event.status === "success" ? "#1E7C3B" : "#B42318",
                      borderColor: event.status === "success" ? "#34C759" : "#FF3B30",
                    }}
                  >
                    {event.status.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}

            {data.events && data.events.length === 0 && (
              <div className="p-12 text-center text-neutral-500">
                No AI usage events yet.
              </div>
            )}
          </div>
        </div>
      )}

      {section === "audit-logs" && (
        <div className="border border-black/10" data-testid="admin-audit-logs">
          <div className="grid grid-cols-12 border-b border-black/10 bg-[#F4F6F8] px-5 py-3 fx-mono-tag">
            <div className="col-span-3">TIME</div>
            <div className="col-span-3">ACTION</div>
            <div className="col-span-2">ACTOR</div>
            <div className="col-span-2">RESOURCE</div>
            <div className="col-span-2 text-right">RESULT</div>
          </div>

          {(data || []).map(entry => (
            <div key={entry.id} className="border-b border-black/5" data-testid={`audit-entry-${entry.id}`}>
              <div className="grid grid-cols-12 px-5 py-4 items-center">
                <div className="col-span-3 text-xs font-mono text-neutral-500">
                  {formatDateTime(entry.created_at)}
                </div>
                <div className="col-span-3 text-sm font-mono font-semibold">
                  {entry.action}
                </div>
                <div className="col-span-2 text-xs">
                  <div className="font-semibold">{entry.actor_role || entry.actor_type}</div>
                  <div className="font-mono text-neutral-500">
                    {entry.actor_user_id ? entry.actor_user_id.slice(0, 8) : "system"}
                  </div>
                </div>
                <div className="col-span-2 text-xs">
                  <div>{entry.resource_type}</div>
                  <div className="font-mono text-neutral-500">
                    {entry.resource_id ? entry.resource_id.slice(0, 12) : "—"}
                  </div>
                </div>
                <div className="col-span-2 text-right">
                  <span
                    className="fx-badge"
                    style={{
                      background: entry.result === "success" ? "#E4F5EA" : "#FFE8E6",
                      color: entry.result === "success" ? "#1E7C3B" : "#B42318",
                      borderColor: entry.result === "success" ? "#34C759" : "#FF3B30",
                    }}
                  >
                    {entry.result.toUpperCase()}
                  </span>
                </div>
              </div>

              <details className="px-5 pb-4 text-xs">
                <summary className="cursor-pointer font-mono text-neutral-500">
                  View safe details
                </summary>
                <pre className="mt-3 overflow-auto bg-[#F4F6F8] p-4 font-mono whitespace-pre-wrap">
                  {JSON.stringify({
                    changes: entry.changes,
                    context: entry.context,
                    error_code: entry.error_code,
                  }, null, 2)}
                </pre>
              </details>
            </div>
          ))}

          {data && data.length === 0 && (
            <div className="p-12 text-center text-neutral-500">
              No audit events yet.
            </div>
          )}
        </div>
      )}

      {section === "analytics" && data && (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-black/10">
            <div className="border-r border-b border-black/10 p-6 bg-[#0B1F3A] text-white" data-testid="analytics-total-clients">
              <div className="fx-mono-tag" style={{color:'#a3a3a3'}}>TOTAL CLIENTS</div>
              <div className="mt-3 text-4xl font-black tracking-tighter font-mono">{data.total_users}</div>
            </div>
            <div className="border-r border-b border-black/10 p-6" data-testid="analytics-total-projects">
              <div className="fx-mono-tag">TOTAL PROJECTS</div>
              <div className="mt-3 text-4xl font-black tracking-tighter font-mono">{data.total_projects}</div>
            </div>
            <div className="border-r border-b border-black/10 p-6" data-testid="analytics-active">
              <div className="fx-mono-tag">ACTIVE</div>
              <div className="mt-3 text-4xl font-black tracking-tighter font-mono">{(data.by_status.approved||0) + (data.by_status.in_progress||0)}</div>
            </div>
            <div className="border-r border-b border-black/10 p-6" data-testid="analytics-completed">
              <div className="fx-mono-tag">COMPLETED</div>
              <div className="mt-3 text-4xl font-black tracking-tighter font-mono">{data.by_status.completed || 0}</div>
            </div>
          </div>
          <div className="border border-black/10 p-8">
            <div className="fx-mono-tag mb-6">STATUS BREAKDOWN</div>
            <div className="space-y-3">
              {["pending","approved","in_progress","completed","rejected"].map(k => {
                const n = data.by_status[k] || 0;
                const max = Math.max(...Object.values(data.by_status), 1);
                return (
                  <div key={k} data-testid={`analytics-bar-${k}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="fx-mono-tag">{k.replace("_"," ").toUpperCase()}</span>
                      <span className="font-mono font-semibold">{n}</span>
                    </div>
                    <div className="h-2 bg-[#F4F6F8] rounded-sm overflow-hidden">
                      <div className="h-full bg-[#0B4DCC]" style={{width: `${(n/max)*100}%`}} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
